import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "wouter";
import CustomLink from "./CustomLink";
import { Menu, X, ChevronDown } from "lucide-react";

interface DropdownItem {
  name: string;
  path: string;
}

interface NavItem {
  name: string;
  path?: string;
  children?: DropdownItem[];
}

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [location] = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navItems: NavItem[] = [
    { name: "Home", path: "/" },
    { name: "Destinations", path: "/destinations" },
    {
      name: "Travel Info",
      children: [
        { name: "Transportation", path: "/transportation" },
        { name: "Accommodation", path: "/accommodation" },
        { name: "Practical Info", path: "/practical-info" },
        { name: "Travel Safety", path: "/travel-safety" },
        { name: "Language Guide", path: "/language-guide" },
      ],
    },
    {
      name: "Explore",
      children: [
        { name: "Culture", path: "/culture" },
        { name: "Cuisine", path: "/cuisine" },
        { name: "Budget Calculator", path: "/budget-calculator" },
        { name: "Packing Checklist", path: "/packing-checklist" },
      ],
    },
    { name: "Blog", path: "/blog" },
    {
      name: "More",
      children: [
        { name: "FAQ", path: "/faq" },
        { name: "Business Directory", path: "/business-directory" },
        { name: "Community Forum", path: "/community-forum" },
        { name: "About Us", path: "/about-us" },
        { name: "Contact", path: "/contact" },
      ],
    },
  ];

  const isActive = (path: string) => location === path;

  const isGroupActive = (item: NavItem) => {
    if (item.path) return isActive(item.path);
    return item.children?.some((child) => isActive(child.path)) ?? false;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setOpenDropdown(null);
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header className="sticky top-0 z-50 bg-white shadow-md">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-3 md:py-4" ref={dropdownRef}>
          {/* Logo */}
          <CustomLink href="/">
            <span className="text-[#E30A17] text-xl md:text-2xl font-poppins font-bold whitespace-nowrap">
              Turkey Guide
            </span>
          </CustomLink>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.name} className="relative">
                  <button
                    onClick={() =>
                      setOpenDropdown(openDropdown === item.name ? null : item.name)
                    }
                    onMouseEnter={() => setOpenDropdown(item.name)}
                    className={`inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition ${
                      isGroupActive(item)
                        ? "text-[#E30A17] bg-red-50"
                        : "text-[#333333] hover:text-[#E30A17] hover:bg-gray-50"
                    }`}
                  >
                    {item.name}
                    <ChevronDown
                      className={`h-3.5 w-3.5 transition-transform ${
                        openDropdown === item.name ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {openDropdown === item.name && (
                    <div
                      className="absolute top-full left-0 mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50"
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      {item.children.map((child) => (
                        <CustomLink
                          key={child.path}
                          href={child.path}
                          className={`block px-4 py-2.5 text-sm transition ${
                            isActive(child.path)
                              ? "text-[#E30A17] bg-red-50 font-medium"
                              : "text-gray-700 hover:text-[#E30A17] hover:bg-gray-50"
                          }`}
                        >
                          {child.name}
                        </CustomLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <CustomLink
                  key={item.path}
                  href={item.path!}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition ${
                    isActive(item.path!)
                      ? "text-[#E30A17] bg-red-50"
                      : "text-[#333333] hover:text-[#E30A17] hover:bg-gray-50"
                  }`}
                >
                  {item.name}
                </CustomLink>
              )
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-[#333333] focus:outline-none p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-2">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.name} className="border-b border-gray-50 last:border-0">
                  <button
                    onClick={() =>
                      setOpenDropdown(openDropdown === item.name ? null : item.name)
                    }
                    className={`w-full flex items-center justify-between py-3 text-sm font-semibold ${
                      isGroupActive(item) ? "text-[#E30A17]" : "text-[#333333]"
                    }`}
                  >
                    {item.name}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        openDropdown === item.name ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {openDropdown === item.name && (
                    <div className="pl-4 pb-2 space-y-1">
                      {item.children.map((child) => (
                        <CustomLink
                          key={child.path}
                          href={child.path}
                          className={`block py-2 text-sm ${
                            isActive(child.path)
                              ? "text-[#E30A17] font-medium"
                              : "text-gray-600 hover:text-[#E30A17]"
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {child.name}
                        </CustomLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <CustomLink
                  key={item.path}
                  href={item.path!}
                  className={`block py-3 text-sm font-semibold border-b border-gray-50 last:border-0 ${
                    isActive(item.path!)
                      ? "text-[#E30A17]"
                      : "text-[#333333] hover:text-[#E30A17]"
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </CustomLink>
              )
            )}
          </div>
        </div>
      )}
    </header>
  );
}
